import { ONBOARDING_TRANSITION_DURATION_MS } from "@/features/user-profile/constants/onboarding-transition";
import { useOnboardingTransition } from "@/features/user-profile/context/onboarding-transition-context";
import { cn } from "@/utils/cn";
import { useIonViewWillEnter } from "@ionic/react";
import { ReactNode, useEffect, useRef, useState } from "react";

interface OnboardingFadeContentProps {
  children: ReactNode;
  className?: string;
}

export const OnboardingFadeContent = ({ children, className }: OnboardingFadeContentProps) => {
  const { isExiting, setIsExiting } = useOnboardingTransition();
  const [hasEntered, setHasEntered] = useState(false);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    // Reset the exit flag here rather than in the navigate callback.
    // By the time this effect runs, the old page is already unmounted from React's
    // tree (and therefore unsubscribed from context), so it won't flash back to
    // opacity-1 when isExiting flips to false.
    setIsExiting(false);
    const raf = requestAnimationFrame(() => setHasEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [setIsExiting]);

  // Pages are kept mounted (Ionic ignores { unmount:true } with routeDirection:"none"),
  // so useEffect above only runs on first mount. On every subsequent entry we need to
  // re-arm the fade-in and clear the shared isExiting flag set by the leaving page.
  useIonViewWillEnter(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    setIsExiting(false);
    setHasEntered(false);
    rafRef.current = requestAnimationFrame(() => {
      setHasEntered(true);
      rafRef.current = null;
    });
  });

  return (
    <div
      className={cn("transition-opacity", className)}
      style={{
        opacity: hasEntered && !isExiting ? 1 : 0,
        transitionDuration: `${ONBOARDING_TRANSITION_DURATION_MS}ms`,
      }}
    >
      {children}
    </div>
  );
};
